import { api } from './api';
import { TerritoryOwnership } from './ownershipService';

export interface MatchupTeam {
    team_id: number;
    team_name: string;
    short_name?: string;
    primary_color: string;
    logo_url?: string;
    territory_count: number;
    total_population: number;
}

export interface LandAtStake {
    total_territories: number;
    total_population: number;
}

export interface FeaturedMatchup {
    espn_game_id: string;
    game_date: string;
    game_name: string;
    status: string;
    status_detail: string;
    venue?: string;
    home_team: MatchupTeam;
    away_team: MatchupTeam;
    land_at_stake: LandAtStake;
}

export interface FeaturedMatchupsResponse {
    success: boolean;
    matchups: FeaturedMatchup[];
    meta?: {
        total_upcoming_games: number;
        total_featured_matchups: number;
        days_ahead: number;
        min_territories_threshold: number;
    };
    error?: string;
}

export interface SimulationTeamResult {
    team_id: number;
    team_name: string;
    short_name?: string;
    primary_color: string;
    logo_url?: string;
    territories_before: number;
    territories_after: number;
    territories_gained?: number;
    territories_lost?: number;
}

export interface TransferredTerritory {
    county_fips: string;
    county_name: string;
    state_name: string;
}

export interface SimulationResult {
    winner: SimulationTeamResult;
    loser: SimulationTeamResult;
    transferred_territories: TransferredTerritory[];
}

export interface SimulatedOwnership extends TerritoryOwnership {
    simulated_transfer?: boolean;
    previous_team_id?: number;
    previous_team_name?: string;
}

export interface SimulationResponse {
    success: boolean;
    simulation?: SimulationResult;
    ownership?: SimulatedOwnership[];
    error?: string;
}

export const matchupService = {
    async getFeaturedMatchups(
        leagueId: string,
        options?: {
            days?: number;
            minTerritories?: number;
            limit?: number;
        }
    ): Promise<FeaturedMatchupsResponse> {
        const queryParams = new URLSearchParams();
        if (options?.days) queryParams.append('days', options.days.toString());
        if (options?.minTerritories) queryParams.append('min_territories', options.minTerritories.toString());
        if (options?.limit) queryParams.append('limit', options.limit.toString());

        const endpoint = `/matchups/${leagueId}/featured${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        const response = await api.get<FeaturedMatchupsResponse>(endpoint);

        if (response.success && response.data) {
            return response.data;
        }

        return {
            success: false,
            matchups: [],
            error: response.error || 'Failed to fetch featured matchups',
        };
    },

    async simulateMatchup(
        leagueId: string,
        winnerTeamId: number,
        loserTeamId: number
    ): Promise<SimulationResponse> {
        const response = await api.post<SimulationResponse>(`/matchups/${leagueId}/simulate`, {
            winner_team_id: winnerTeamId,
            loser_team_id: loserTeamId,
        });

        if (response.success && response.data) {
            return response.data;
        }

        return {
            success: false,
            error: response.error || 'Failed to simulate matchup',
        };
    },
};
